import React, { useCallback, useRef, useState } from 'react';

/**
 * Dependency-free drag-and-drop reordering for the program editors
 * (required documents, pipeline stages, training/marketing resources).
 *
 * Uses the native HTML5 drag events — the project has no DnD library and this
 * avoids adding one. Keyboard reordering is provided separately by the
 * consumers (move up / move down buttons) so the feature is not mouse-only,
 * which the accessibility part of the brief requires.
 */

export interface DragHandlers {
  draggingIndex: number | null;
  overIndex: number | null;
  getItemProps: (index: number) => {
    draggable: boolean;
    onDragStart: (event: React.DragEvent) => void;
    onDragEnter: (event: React.DragEvent) => void;
    onDragOver: (event: React.DragEvent) => void;
    onDragEnd: () => void;
    onDrop: (event: React.DragEvent) => void;
    'data-dragging': boolean;
    'data-drag-over': boolean;
  };
}

export function useDragReorder(onReorder: (from: number, to: number) => void): DragHandlers {
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const fromRef = useRef<number | null>(null);

  const finish = useCallback(() => {
    setDraggingIndex(null);
    setOverIndex(null);
    fromRef.current = null;
  }, []);

  const getItemProps = useCallback(
    (index: number) => ({
      draggable: true,
      'data-dragging': draggingIndex === index,
      'data-drag-over': overIndex === index && draggingIndex !== index,
      onDragStart: (event: React.DragEvent) => {
        fromRef.current = index;
        setDraggingIndex(index);
        event.dataTransfer.effectAllowed = 'move';
        // Firefox refuses to start a drag without data set.
        event.dataTransfer.setData('text/plain', String(index));
      },
      onDragEnter: (event: React.DragEvent) => {
        event.preventDefault();
        setOverIndex(index);
      },
      onDragOver: (event: React.DragEvent) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      },
      onDragEnd: finish,
      onDrop: (event: React.DragEvent) => {
        event.preventDefault();
        const from = fromRef.current ?? Number(event.dataTransfer.getData('text/plain'));
        if (Number.isInteger(from) && from !== index) onReorder(from, index);
        finish();
      },
    }),
    [draggingIndex, overIndex, onReorder, finish]
  );

  return { draggingIndex, overIndex, getItemProps };
}

/** Shared visual treatment for a draggable row. */
export function dragRowClass(isDragging: boolean, isOver: boolean): string {
  return [
    'group relative rounded-xl border bg-white transition',
    isDragging ? 'opacity-40' : '',
    isOver ? 'border-blue-400 ring-2 ring-blue-500/20' : 'border-slate-200',
  ]
    .filter(Boolean)
    .join(' ');
}

/** Move helper shared by the keyboard controls. */
export function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}
